import React, { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton, Stepper, Step, StepLabel,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';

import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { get } from 'lodash';
import useStyles from '../modal/styles';
import { TimesIcon } from '../icon/solid';
import FormSubmit from './actions/FormSubmit';
import FormReset from './actions/FormReset';
import { useId } from '../../hooks';
import { Button } from '../index';
import { ReactHookFormModalProps, StepType } from './types';
import FormNextStep from './actions/FormNextStep';

type Props = Omit<ReactHookFormModalProps, 'children'> & {
  steps: StepType[];
  nextStepValidFields?: string[];
  closeOnCancel?: boolean;
  paperClassName?: string;
};

const ReactHookFormStepModal = ({
  open,
  formSettings,
  title,
  onCloseHandler,
  steps,
  nextStepValidFields = [],
  closeOnCancel = true,
  paperClassName = '',
}: Props) => {
  const id = useId();
  const classes = useStyles();
  const {
    validationSchema,
    initialValues,
    creatingMode,
    submitTitle,
    onSubmit,
    loading,
    hideCancel = false,
  } = formSettings;

  const form = useForm({
    defaultValues: initialValues || {},
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    shouldUnregister: false,
  });

  const {
    reset,
    handleSubmit,
    formState: { isValid },
    errors,
  } = form;

  const submitHandler = useCallback((data: any) => {
    onSubmit(data);
    // onCloseHandler();
  }, [onSubmit]);

  // const [openState, setOpenState] = useState(open);
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());
  const isStepSkipped = (step: number) => skipped.has(step);

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const closeHandler = useCallback(() => {
    // setOpenState(false);
    onCloseHandler();
  }, [onCloseHandler]); // setOpenState,

  // useEffect(() => setOpenState(open), [open, setOpenState]);


  const blockNext = nextStepValidFields?.some((fieldName) => get(errors, fieldName));

  return (
    <Dialog
      open={open}
      onEscapeKeyDown={closeHandler}
      aria-labelledby={id}
      disableBackdropClick
      PaperProps={{ className: `${classes.modal} ${classes.stepModalPaper} ${paperClassName}` }}
      fullWidth
      style={{ zIndex: 2147483100 }}
    >
      <DialogTitle id={id}>
        {title}
        <IconButton aria-label="close" className={classes.closeButton} onClick={closeHandler}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <FormProvider {...form}>
          <div className={`${classes.stepModalContainer} ${classes.formContent}`}>
            <Stepper activeStep={activeStep} className={classes.stepModalStepper}>
              {steps.map((step) => (
                <Step key={step.title}>
                  <StepLabel>{step.title}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {steps[activeStep].content}
          </div>
        </FormProvider>
      </DialogContent>
      <DialogActions className={clsx(classes.dialogActions, classes.stepModalDialogActions, {
        [classes.singleAction]: hideCancel,
      })}
      >

        {activeStep === steps.length - 1 && (
          <FormSubmit
            submitHandler={handleSubmit((d) => submitHandler(d))}
            className={classes.panelButton}
            variant="contained"
            color="primary"
            disabled={!isValid || loading}
            // withHotkey
          >
            <span className={classes.buttonText}>
              {submitTitle || creatingMode ? 'Create' : 'Save'}
            </span>
          </FormSubmit>
        )}

        {!hideCancel
        && (
          <FormReset
            className={clsx(classes.cancelButton, classes.panelButton)}
            variant="contained"
            color="default"
            withHotkey
            closeDialog={closeHandler}
            closeOnCancel={closeOnCancel}
            resetHandler={reset}
          >

            <TimesIcon className={classes.buttonIcon} />
            <span className={classes.buttonText}>Cancel</span>
          </FormReset>
        )}

        {activeStep > 0 && (
          <Button
            variant="contained"
            onClick={handleBack}
            className={`${classes.backButton} ${classes.panelButton}`}
          >
            <span className={classes.buttonText}>
                   Back
            </span>
          </Button>
        )}

        {activeStep < steps.length - 1 && (
          <FormNextStep
            disabled={blockNext}
            variant="contained"
            color="primary"
            onClick={handleNext}
            className={`${classes.panelButton} ${classes.nextButton}`}
          >
            <span className={classes.buttonText}>
                  Next
            </span>
          </FormNextStep>
        )}


      </DialogActions>
    </Dialog>
  );
};

export default ReactHookFormStepModal;
